import React, { useState } from "react";
import { Navigate, useLocation, useNavigate, useSearchParams } from "react-router-dom";
import { Lock } from "lucide-react";
import { usePortalAuth } from "../../contexts/PortalAuthContext";
import usePageMeta from "../../hooks/usePageMeta";

const ROLE_LANDING = {
  admin: "/projects/admin",
  project_manager: "/projects/manager",
  site_engineer: "/projects/site",
  client: "/projects/my-projects",
  consultant: "/projects/my-projects",
  subcontractor: "/projects/my-projects",
};

// Safe landing for users with unknown / missing role. Must NOT be
// /projects/admin — that triggers the PortalDashboard non-admin bounce
// which redirects back to /projects → PortalLogin → /projects/admin →
// infinite replaceState() loop. /projects/my-projects renders the dashboard
// in "my" variant for any logged-in user without role-based bouncing.
const SAFE_LANDING = "/projects/my-projects";

export default function PortalLogin() {
  usePageMeta({
    title: "Project Portal Login | Time Furniture",
    description: "Secure project portal access for Time Furniture clients, consultants and team.",
    canonical: "https://www.timefurniture.ae/projects",
  });
  const { user, login } = usePortalAuth();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [err, setErr] = useState("");
  const [busy, setBusy] = useState(false);
  const navigate = useNavigate();
  const loc = useLocation();
  const [params] = useSearchParams();
  const next = params.get("next");
  // Latch the redirect to a single Navigate per mount using react-router's
  // own Navigate component (whose internal useEffect+useRef pattern fires
  // navigate() exactly once on mount). Combined with SAFE_LANDING below,
  // this prevents the production /projects ↔ /projects/admin replaceState
  // loop ("more than 100 times per 10 seconds") triggered by users whose
  // cached role doesn't match the server response.

  // Compute target landing once per render. Falls back to SAFE_LANDING
  // (not /projects/admin) when role is missing/unknown to avoid the
  // login → /projects/admin → bounce → login redirect cycle.
  const targetPath = user
    ? (next || ROLE_LANDING[user.role] || SAFE_LANDING)
    : null;

  if (user && targetPath && targetPath !== loc.pathname) {
    return <Navigate to={targetPath} replace />;
  }

  const onSubmit = async (e) => {
    e.preventDefault();
    setErr("");
    setBusy(true);
    try {
      const data = await login(email.trim().toLowerCase(), password);
      navigate(next || data.landing || ROLE_LANDING[data.user.role] || SAFE_LANDING, { replace: true });
    } catch (e) {
      setErr(e.message || "Login failed");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-[#FAF5EC] px-5">
      <div className="w-full max-w-[440px]">
        <div className="text-center mb-10">
          <p className="text-[10px] tracking-[0.32em] uppercase text-[#7A6B5F] mb-3">
            Secure Project Portal
          </p>
          <h1 className="font-serif-display text-3xl md:text-4xl tracking-tight text-[#6E1F2C]">
            Time · Furniture
          </h1>
          <p className="mt-3 text-[10px] tracking-[0.36em] uppercase text-[#D4A963]">
            UAE Since 1993
          </p>
        </div>
        <form
          onSubmit={onSubmit}
          data-testid="portal-login-form"
          className="bg-white border border-[#6E1F2C]/15 p-8 md:p-10"
        >
          <p className="flex items-center gap-2 text-[10px] tracking-[0.32em] uppercase text-[#6E1F2C] mb-7">
            <Lock size={12} /> Sign in
          </p>

          <label htmlFor="login-email" className="block text-[10px] tracking-[0.3em] uppercase text-[#7A6B5F] mb-2">
            Email
          </label>
          <input
            id="login-email"
            data-testid="portal-login-email"
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            autoComplete="email"
            required
            className="w-full bg-[#FAF5EC] border border-[#6E1F2C]/20 px-4 py-3 mb-5 text-[#1C110C] focus:outline-none focus:border-[#6E1F2C]"
          />

          <label htmlFor="login-pw" className="block text-[10px] tracking-[0.3em] uppercase text-[#7A6B5F] mb-2">
            Password
          </label>
          <input
            id="login-pw"
            data-testid="portal-login-password"
            type="password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            autoComplete="current-password"
            required
            className="w-full bg-[#FAF5EC] border border-[#6E1F2C]/20 px-4 py-3 mb-6 text-[#1C110C] focus:outline-none focus:border-[#6E1F2C]"
          />

          {err && (
            <p data-testid="portal-login-error" className="text-xs text-[#A3141A] mb-5">
              {err}
            </p>
          )}

          <button
            type="submit"
            data-testid="portal-login-submit"
            disabled={busy}
            className="w-full bg-[#6E1F2C] text-[#FAF5EC] px-6 py-4 text-[11px] tracking-[0.28em] uppercase hover:bg-[#56151F] transition-colors disabled:opacity-60"
          >
            {busy ? "Signing in…" : "Sign in"}
          </button>

          <p className="mt-7 text-[10px] text-[#7A6B5F] leading-relaxed text-center">
            Access is invite-only.<br />Please contact your project manager if you need help signing in.
          </p>
        </form>
      </div>
    </div>
  );
}
